import { useState } from "react";
import { Button, NumberInput, Select, type SelectOption } from "@/deps/ui-library";
import { Dialog } from "@/components/Dialog";
import type { IsoDate, Money } from "@/api/types";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";

const paisa = (value: string) => Math.round(Number(value || "0") * 100);
const MONEY = { useGrouping: false, minimumFractionDigits: 2, maximumFractionDigits: 2 };

function chosen(details: { value: SelectOption | SelectOption[] | null }): string {
  const option = Array.isArray(details.value) ? details.value[0] : details.value;
  return option ? String(option.value) : "";
}

const lesser = (a: string, b: string) => (paisa(a) < paisa(b) ? a : b);

export interface OfferedCredit {
  sourceId: string;
  number: string;
  /** "advance", "debit note" or "credit note". */
  kindLabel: string;
  date: IsoDate;
  available: Money;
}

/** Uses a credit the other party already holds against one document (ADR-0031, ADR-0034). */
export function UseCreditDialog({
  documentNumber,
  documentLabel,
  description,
  balance,
  credits,
  pending,
  error,
  onUse,
  onClose,
}: {
  documentNumber: string | null;
  /** "bill" or "invoice", for the sentence that refuses too much. */
  documentLabel: string;
  description: string;
  balance: Money;
  credits: OfferedCredit[];
  pending: boolean;
  error: unknown;
  onUse: (sourceId: string, amount: Money) => void;
  onClose: () => void;
}) {
  const first = credits[0];
  const [sourceId, setSourceId] = useState(first?.sourceId ?? "");
  const [amount, setAmount] = useState(first ? lesser(first.available, balance) : "");
  const credit = credits.find((c) => c.sourceId === sourceId);
  const over =
    credit !== undefined &&
    (paisa(amount) > paisa(credit.available) || paisa(amount) > paisa(balance));

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Use a credit on ${documentNumber}`}
      description={description}
      footer={
        <Button
          colorScheme="primary"
          variant="solid"
          size="sm"
          disabled={credit === undefined || paisa(amount) <= 0 || over || pending}
          onClick={() => onUse(credit!.sourceId, (paisa(amount) / 100).toFixed(2))}
        >
          {pending ? "Using" : "Use credit"}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <Select
          label="Credit"
          required
          options={credits.map((option) => ({
            value: option.sourceId,
            label: `${option.number} · ${option.kindLabel} · ${formatDate(option.date)} · ${formatAmount(option.available)}`,
          }))}
          value={sourceId}
          onChange={(details) => {
            const next = chosen(details);
            setSourceId(next);
            const picked = credits.find((c) => c.sourceId === next);
            if (picked) setAmount(lesser(picked.available, balance));
          }}
          showClearButton={false}
        />
        <NumberInput
          label="Amount to use"
          required
          invalid={over}
          value={amount}
          onValueChange={(details) => setAmount(details.value)}
          formatOptions={MONEY}
          config={{ hideTriggers: true }}
        />
        {over ? (
          <p role="alert" className="text-bad-text text-xs">
            That is more than the credit has left, or more than this {documentLabel} owes.
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="text-bad-text text-xs">
            {error instanceof Error ? error.message : "That credit could not be used."}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
